/* Bewertung einer Karte: eine Zahl von 0 bis 100 und die Teilwerte dahinter, damit sich Läufe vergleichen lassen.
 * Eingabe sind die Ergebnisse von Prüfung (services/check.ts) und Compile; ohne DOM, Dateien und Prozesse.
 * Formel (bewusst einfach und offen):
 *   validity     100 − 30 je Fehler − 6 je Warnung der Korrektheit (core.*, lint.*, chk.*)
 *   performance  100 − 25 je Fehler − 8 je Warnung der Leistung (perf.*, cx.*)
 *   limits       100, solange die höchste Auslastung (Engine-Grenzen, Clipnodes, Entities) unter 50 % liegt; danach −2 je Prozentpunkt;
 *                weiche Grenzen (Compiler-Erweiterungen) zählen zu 75 %
 *   build        100 bei erfolgreichem Compile, 40 bei Leck, 0 bei Fehler; ohne Compile entfällt der Teil
 *   total        0,4 validity + 0,2 performance + 0,2 limits + 0,2 build (ohne build neu gewichtet)
 *   Sperren      Leck, Spieler im Solid/außerhalb, fehlender Spawn: total höchstens 40; Compile gescheitert: 0 */
import type { CheckLevel } from '../model/types.ts';
import type { BspLimitRow } from '../analysis/limits.ts';
import type { ClipLimits } from './check.ts';

export interface ScoreItem { id?: string; level: CheckLevel }
export interface BuildInfo { ok: boolean; leak?: boolean; ms?: number }
export interface ScoreInput {
  items: ScoreItem[];
  clip?: ClipLimits | { error: string } | null;
  bsp?: BspLimitRow[] | null;
  build?: BuildInfo | null;
}
export interface Scorecard {
  version: 1;
  total: number;
  parts: { validity: number; performance: number; limits: number; build: number | null };
  gates: string[];
  counts: { err: number; warn: number };
  /** Anzahl je Befund-ID (nur Warnungen und Fehler) */
  findings: Record<string, number>;
  /** IDs der Befunde mit Stufe Fehler */
  errorIds: string[];
  metrics: { worstLimitRatio: number; clipRatio: number | null; edicts: number | null; buildMs: number | null };
}

/** Weiche Grenzen (Erweiterungen des Compilers, etwa Lightmap-Speicher) zählen mit diesem Faktor, sie sind keine harte Engine-Grenze */
export const SOFT_WEIGHT = 0.75;
const PERF = /^(perf|cx)\./;
const GATE_IDS: Record<string, string> = { 'core.c_leak': 'leak', 'core.c_start_solid': 'start-solid', 'core.c_start_outside': 'start-outside', 'core.c_start_missing': 'no-start', 'core.c_no_spawns': 'no-spawns' };
const clamp = (n: number): number => Math.max(0, Math.min(100, n));
const round1 = (n: number): number => Math.round(n * 10) / 10;

export function scoreOf(input: ScoreInput): Scorecard {
  let validity = 100, performance = 100, err = 0, warn = 0;
  const findings: Record<string, number> = {}, errorIds: string[] = [], gates: string[] = [];
  for (const it of input.items) {
    if (it.level !== 'warn' && it.level !== 'err') continue;
    const id = it.id || '?', perf = PERF.test(id), isErr = it.level === 'err';
    findings[id] = (findings[id] || 0) + 1;
    if (isErr) { err++; if (!errorIds.includes(id)) errorIds.push(id); } else warn++;
    if (perf) performance -= isErr ? 25 : 8; else validity -= isErr ? 30 : 6;
    if (GATE_IDS[id] && !gates.includes(GATE_IDS[id])) gates.push(GATE_IDS[id]);
  }
  // höchste Auslastung: BSP-Zeilen, sonst Clipnode-Schätzung; Entities (Grenze etwa 900) zählen mit
  let worst = 0, clipRatio: number | null = null, edicts: number | null = null;
  if (input.bsp) for (const r of input.bsp) worst = Math.max(worst, r.soft ? r.ratio * SOFT_WEIGHT : r.ratio);
  const clip = input.clip && !('error' in input.clip) ? input.clip : null;
  if (clip) { clipRatio = clip.est.est / clip.est.max; edicts = clip.edicts; if (!input.bsp) worst = Math.max(worst, clipRatio); worst = Math.max(worst, clip.edicts / 900); }
  const limits = clamp(worst <= 0.5 ? 100 : 100 - (worst - 0.5) * 200);
  let build: number | null = null;
  if (input.build) {
    build = input.build.ok ? (input.build.leak ? 40 : 100) : 0;
    if (input.build.leak && !gates.includes('leak')) gates.push('leak');
    if (!input.build.ok) gates.push('compile-failed');
  }
  const v = clamp(validity), p = clamp(performance);
  let total = build === null ? 0.5 * v + 0.25 * p + 0.25 * limits : 0.4 * v + 0.2 * p + 0.2 * limits + 0.2 * build;
  if (gates.some((g) => g !== 'compile-failed')) total = Math.min(total, 40);
  if (gates.includes('compile-failed')) total = 0;
  return {
    version: 1, total: round1(total), parts: { validity: v, performance: p, limits: round1(limits), build },
    gates, counts: { err, warn }, findings, errorIds,
    metrics: { worstLimitRatio: Math.round(worst * 1000) / 1000, clipRatio: clipRatio === null ? null : Math.round(clipRatio * 1000) / 1000, edicts, buildMs: input.build && input.build.ms !== undefined ? input.build.ms : null }
  };
}
